import { Fragment, useState } from 'react'
import { Transition } from '@headlessui/react'

export type AlertType = 'error' | 'warning' | 'success' | 'info'

type AlertProps = {
	type: AlertType
	title: string
	text?: string
}

// Single source of truth for banner/toast icons; InlineAlert reuses it.
export const ALERT_ICON: Record<AlertType, string> = {
	success: 'fa-solid fa-circle-check',
	info: 'fa-solid fa-circle-info',
	error: 'fa-solid fa-circle-xmark',
	warning: 'fa-solid fa-circle-exclamation',
}

const iconColour: Record<AlertType, string> = {
	success: 'text-success-500',
	info: 'text-algae-500',
	error: 'text-error-500',
	warning: 'text-warning-500',
}

const iconType = (type: AlertType) => `${iconColour[type]} ${ALERT_ICON[type]}`

function Alert(props: AlertProps) {
	const [show, setShow] = useState(true)

	return (
		<div
			aria-live='assertive'
			className='pointer-events-none fixed inset-0 flex items-end px-4 py-6'
		>
			<div className='flex w-full flex-col items-end space-y-4'>
				<Transition
					show={show}
					as={Fragment}
					enter='transform ease-out duration-300 transition'
					enterFrom='translate-y-2 opacity-0'
					enterTo='translate-y-0 opacity-100'
					leave='transition ease-in duration-100'
					leaveFrom='opacity-100'
					leaveTo='opacity-0'
				>
					<div className='pointer-events-auto w-full max-w-sm overflow-hidden rounded-md bg-white ring-1 ring-black text-white ring-opacity-5'>
						<div className='p-4'>
							<div className='flex items-start justify-center'>
								<div className='flex-shrink-0'>
									<span className={`h-xl w-xl ${iconType(props.type)}`} aria-hidden='true'></span>
								</div>
								<div className='ml-3 w-0 flex-1 pt-0.5'>
									<p className='text-sm text-charcoal-900'>{props.title}</p>
									{props.text && <p className='mt-1 text-sm text-charcoal-100'>{props.text}</p>}
								</div>
								<div className='ml-4 flex flex-shrink-0'>
									<button
										type='button'
										className='inline-flex rounded-md bg-white text-charcoal-500'
										onClick={() => {
											setShow(false)
										}}
									>
										<span className='sr-only'>Close</span>
										<span className='fa-solid fa-xmark h-5 w-5' aria-hidden='true'></span>
									</button>
								</div>
							</div>
						</div>
					</div>
				</Transition>
			</div>
		</div>
	)
}
export default Alert
